/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-var-requires */

import { useState, useEffect, useRef, useCallback } from 'react';
import { SecureFields } from '@gr4vy/secure-fields-react';
import { QueryOptions } from '@apollo/client';

import { CreditCardPurposeFields } from './CreditCardPurposeFields';
import { SecurePaymentFields } from './SecurePaymentFields';
import { markEmptyFieldsInvalid } from './helper';
import {
  GravyVaultSubmitRefState,
  type ISecurePaymentFormProps,
  CheckoutSessionQueryData,
  CheckoutSessionQueryVariables,
  CreateQueryConfigParams,
  SecureFieldsEnvironmentType,
} from './SecurePaymentForm.types';
import type { IFormStatus } from './SecurePaymentFields/SecurePaymentFields.types';
import { StyledError, StyledPreferredCardCheckbox } from './SecurePaymentForm.styles';
import { getNextMiApolloClient, getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { CardVaultFailureData } from '@gr4vy/secure-fields-react/lib/types';
import { SecurePaymentSubmit } from './SecurePaymentFields/SecurePaymentSubmit';
import { cardTypes } from './constants';
const CheckoutSessionGraphql = require('./mutation/checkoutSession.graphql');
const operationSignatures = [
  {
    operationName: 'CreateGravyCheckoutSession',
    signature: '266283b7cf395e52953eb6152ffbf211184f276c406ed8c98225c868ab768b2e',
  },
];
export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export const SecurePaymentForm = ({
  propertyId = '',
  onSecureFieldsValidation,
  onError,
  secureFields,
  hidePreferredCard,
  hidePurposeOptions,
  primaryCard,
  secureFieldError,
  purposeBlockClassName = '',
  showZipCodeField,
  showCvvNumberField,
  setCheckoutSessionId,
}: ISecurePaymentFormProps) => {
  const { preferences } = secureFields;
  const [cardPurpose, setCardPurpose] = useState('P');
  const [isCheckoutSessionError, setCheckoutSessionError] = useState(false);
  const [gr4vySessionId, setGr4vySessionId] = useState('');
  const [secureFieldsInstance, setSecureFieldsInstance] = useState<any>(null);
  const [formStatus, setFormStatus] = useState<IFormStatus>({
    fields: {
      number: { valid: true, empty: true },
      expiryDate: { valid: true, empty: true },
      securityCode: { valid: true, empty: true },
      postalCode: { valid: true, empty: true },
    },
    complete: false,
  });
  const session = canUseDOM && window?.sessionData?.data ? window?.sessionData : window?.sessionData?.cacheData;

  let serviceId;
  let serviceEnv;
  // In production, the payment service ID and environment are injected into the DOM by the server. In development, they are read from environment variables.
  if (canUseDOM) {
    const envTag = document.getElementById('__SERVERENV__');
    if (envTag) {
      const envObject = JSON.parse(envTag.innerHTML);
      serviceId = envObject['NEXT_PUBLIC_PAYMENT_SERVICE_ID'];
      serviceEnv = envObject['NEXT_PUBLIC_PAYMENT_SERVICE_ENV'];
    }
  }

  const cardTypeRef = useRef('');
  const nickNameRef = useRef('');
  const cardPurposeRef = useRef('P');
  const cardPreferredRef = useRef(false);
  const vaultSubmitRef = useRef<GravyVaultSubmitRefState>({});

  const paymentServiceId = serviceId || process.env['NEXT_PUBLIC_PAYMENT_SERVICE_ID'] || '';
  const paymentServiceEnv = serviceEnv || process.env['NEXT_PUBLIC_PAYMENT_SERVICE_ENV'] || '';

  useEffect(() => {
    if (formStatus.complete) {
      onSecureFieldsValidation(true);
    } else {
      onSecureFieldsValidation(false);
    }
  }, [formStatus, onSecureFieldsValidation]);

  const onCardVaultSuccessHandler = useCallback(() => {
    const CardVaultResultData = {
      makePreferred: cardPreferredRef.current,
      selectedPurpose: cardPurposeRef.current,
      checkoutSessionId: gr4vySessionId,
      nickName: nickNameRef.current,
      cardType: cardTypeRef.current,
    };
    vaultSubmitRef.current.resolve?.(CardVaultResultData);
    vaultSubmitRef.current.onSubmitSuccessCallback?.();
  }, [cardTypeRef.current, cardPreferredRef.current, cardPurposeRef.current, nickNameRef.current, gr4vySessionId]);

  const onCardVaultFailureHandler = (data: CardVaultFailureData) => {
    vaultSubmitRef.current.reject?.(data);
    vaultSubmitRef.current.onSubmitError?.(data);

    // trigger Inline Error on card vault failure
    setFormStatus(prev => {
      const updatedFields = markEmptyFieldsInvalid(prev.fields);
      return { ...prev, fields: updatedFields };
    });
  };

  const env = getDeployedEnvType();
  const MiApolloClient = getNextMiApolloClient(operationSignatures, env);

  function createQueryConfig<TVariables>({
    query,
    variables,
    requestId,
  }: CreateQueryConfigParams<TVariables>): QueryOptions<any, TVariables> {
    return {
      query,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables,
      context: {
        headers: { 'x-request-id': requestId },
      },
    };
  }

  useEffect(() => {
    const createCheckoutSession = async () => {
      try {
        const { data } = await MiApolloClient.query<CheckoutSessionQueryData, CheckoutSessionQueryVariables>(
          createQueryConfig({
            query: CheckoutSessionGraphql,
            variables: { input: { propertyCode: propertyId } },
            requestId: session?.data?.['x-request-id'],
          })
        );

        const checkoutSessionId = data.commerce.payment.createGravyCheckoutSession.sessionId;
        setCheckoutSessionId(checkoutSessionId);
        setGr4vySessionId(checkoutSessionId);
        onError(false);
      } catch (err) {
        onError(true);
        setCheckoutSessionError(true);
        console.error('create_checkout_session: failed to create the checkout session, ', err);
      }
    };

    createCheckoutSession();
  }, []);

  useEffect(() => {
    return () => {
      secureFieldsInstance?.destroy?.();
    };
  }, [secureFieldsInstance]);

  const setPurpose = (_e: Event, purpose: string) => {
    cardPurposeRef.current = purpose;
    setCardPurpose(purpose);
  };

  const nickNameChangeHandler = (name: string) => {
    nickNameRef.current = name;
  };

  const cardNumberChangeHandler = (type: keyof typeof cardTypes) => {
    if (cardTypeRef.current !== cardTypes[type]) {
      cardTypeRef.current = cardTypes[type] || '';
    }
  };

  const handleBeforeVaultSubmit = useCallback(() => {
    setFormStatus(prev => {
      const updatedFields = markEmptyFieldsInvalid(prev.fields);
      return { ...prev, fields: updatedFields };
    });
  }, []);

  if (isCheckoutSessionError) {
    return <StyledError>{secureFieldError}</StyledError>;
  }

  if (!gr4vySessionId) {
    // replace with skeleton
    return <StyledError />;
  }

  return (
    <SecureFields
      key={`secure-fields-${showZipCodeField}`}
      gr4vyId={paymentServiceId}
      environment={paymentServiceEnv as SecureFieldsEnvironmentType}
      sessionId={gr4vySessionId}
      debug
      onReady={instance => setSecureFieldsInstance(instance)}
      onFormChange={(data: IFormStatus) => setFormStatus(data)}
      onCardVaultSuccess={onCardVaultSuccessHandler}
      onCardVaultFailure={onCardVaultFailureHandler}
    >
      {!hidePurposeOptions && (
        <CreditCardPurposeFields
          {...(preferences ?? {})}
          purposeBlockClassName={purposeBlockClassName}
          setPurpose={setPurpose}
          selectedPurposeCode={cardPurpose}
        />
      )}
      <SecurePaymentFields
        cardProps={secureFields}
        formStatus={formStatus}
        onNickNameChange={nickNameChangeHandler}
        showZipCodeField={showZipCodeField}
        showCvvNumberField={showCvvNumberField}
        onCardNumberChange={cardNumberChangeHandler}
      />
      <SecurePaymentSubmit vaultSubmitRef={vaultSubmitRef} onBeforeSubmit={handleBeforeVaultSubmit} />

      {!hidePreferredCard && (
        <StyledPreferredCardCheckbox className="pb-md-0 mt-3">
          <div className="checkbox">
            <input
              id="preferredCard"
              name="preferredCard"
              type="checkbox"
              value="preferred"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                cardPreferredRef.current = event.target.checked;
              }}
            />
            <label htmlFor="preferredCard">{primaryCard}</label>
          </div>
        </StyledPreferredCardCheckbox>
      )}
    </SecureFields>
  );
};
